import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Save, Send, CheckSquare, AlertTriangle, CheckCircle } from 'lucide-react';
import { useApi } from './DashboardLayout';

function formatDate(d) {
  if (!d) return '';
  const parts = d.substring(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d;
}

export default function SurveyFormPage({ user }) {
  const { apiFetch } = useApi();
  const [survey, setSurvey] = useState(null);
  const [offerTypes, setOfferTypes] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [entries, setEntries] = useState([]);
  const [confirmedTexts, setConfirmedTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    // Get active survey for this provider
    const surveyR = await apiFetch('/api/surveys/active-for-provider');
    const surveyData = await surveyR?.json();

    if (!surveyData || !surveyData.id) {
      setSurvey(null);
      setLoading(false);
      return;
    }
    setSurvey(surveyData);

    // Get offer types and existing submission in parallel
    const [typesR, subR] = await Promise.all([
      apiFetch(`/api/surveys/${surveyData.id}/my-offer-types`),
      apiFetch(`/api/surveys/${surveyData.id}/my-submission`)
    ]);
    const types = await typesR?.json();
    const sub = await subR?.json();

    // types is now [{offer_type, group_count}, ...] with group_count = certified offer count
    const typesArray = Array.isArray(types) ? types : [];
    const typeNames = typesArray.map(t => typeof t === 'string' ? t : t.offer_type);
    const groupCounts = {};
    typesArray.forEach(t => {
      if (typeof t === 'string') { groupCounts[t] = 0; }
      else { groupCounts[t.offer_type] = t.group_count || 0; }
    });

    if (Array.isArray(types)) setOfferTypes(typeNames);
    setSubmission(sub);

    // Initialize entries from existing submission or from offer types
    if (sub && sub.data && sub.data.length > 0) {
      const typeSet = new Set(typeNames);
      const validExisting = sub.data.filter(e => typeSet.has(e.offer_type));
      const existingTypes = new Set(validExisting.map(e => e.offer_type));
      // Override group_count with certified count
      const merged = validExisting.map(e => ({ ...e, group_count: groupCounts[e.offer_type] || e.group_count }));
      for (const t of typeNames) {
        if (!existingTypes.has(t)) {
          merged.push({ offer_type: t, group_count: groupCounts[t] || 0, male_count: 0, female_count: 0, total_count: 0 });
        }
      }
      setEntries(merged);
      setConfirmedTexts(sub.confirmed_texts || []);
    } else {
      const initial = typeNames.map(t => ({
        offer_type: t,
        group_count: groupCounts[t] || 0,
        male_count: 0,
        female_count: 0,
        total_count: 0,
      }));
      setEntries(initial);
      setConfirmedTexts([]);
    }
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateEntry = (idx, field, value) => {
    setEntries(prev => {
      const updated = [...prev];
      const numVal = Math.max(0, parseInt(value) || 0);
      updated[idx] = { ...updated[idx], [field]: numVal };
      // Auto-calculate total
      if (field === 'male_count' || field === 'female_count') {
        const male = field === 'male_count' ? numVal : updated[idx].male_count;
        const female = field === 'female_count' ? numVal : updated[idx].female_count;
        updated[idx].total_count = male + female;
      }
      return updated;
    });
  };

  const toggleConfirmation = (ctId) => {
    setConfirmedTexts(prev => {
      if (prev.includes(ctId)) return prev.filter(id => id !== ctId);
      return [...prev, ctId];
    });
  };

  const handleSave = async (action = 'save') => {
    setSaving(true);
    setMessage(null);

    // For submit: check all required confirmation texts are checked
    if (action === 'submit') {
      const requiredTexts = (survey?.confirmation_texts || []).filter(t => t.required);
      const allConfirmed = requiredTexts.every(t => confirmedTexts.includes(t.id));
      if (!allConfirmed) {
        setMessage({ type: 'error', text: 'Bitte bestätigen Sie alle Pflicht-Bestimmungen.' });
        setSaving(false);
        return;
      }
    }

    const r = await apiFetch(`/api/surveys/${survey.id}/my-submission`, {
      method: 'POST',
      body: JSON.stringify({
        data: entries,
        confirmed_texts: confirmedTexts,
        action: action,
      })
    });
    const d = await r?.json();
    setSaving(false);
    if (d?.status) {
      setMessage({ type: 'success', text: d.message });
      loadData();
    } else {
      setMessage({ type: 'error', text: d?.detail || 'Fehler beim Speichern' });
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const isReadOnly = submission?.status === 'fixed';
  const isExpired = survey && today > survey.end_date && !(submission?.deadline_extension && today <= submission.deadline_extension);
  const canEdit = survey && !isReadOnly && !isExpired;

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  if (!survey) {
    return (
      <div data-testid="survey-form-empty">
        <h2 style={{ fontSize: 20, marginBottom: 12 }}>Bestandserhebung</h2>
        <div className="of-card" style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
          <AlertTriangle size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
          <p style={{ margin: '0 0 4px', fontSize: 14 }}>Derzeit keine aktive Erhebung</p>
          <p style={{ margin: 0, fontSize: 12 }}>Es gibt aktuell keinen offenen Erhebungszeitraum.</p>
        </div>
      </div>
    );
  }

  // Calculate grand totals
  const totalMale = entries.reduce((s, e) => s + (e.male_count || 0), 0);
  const totalFemale = entries.reduce((s, e) => s + (e.female_count || 0), 0);
  const totalAll = entries.reduce((s, e) => s + (e.total_count || 0), 0);
  const totalGroups = entries.reduce((s, e) => s + (e.group_count || 0), 0);

  return (
    <div data-testid="survey-form">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20, flex: 1 }}>{survey.title}</h2>
        {submission?.status === 'fixed' && (
          <span className="badge badge-success" data-testid="status-fixed" style={{ fontSize: 13, padding: '4px 12px' }}>
            <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Fixiert
          </span>
        )}
        {submission?.status === 'submitted' && (
          <span className="badge badge-warning" data-testid="status-submitted" style={{ fontSize: 13, padding: '4px 12px' }}>
            Abgegeben – noch nicht fixiert
          </span>
        )}
      </div>

      {/* Info Banner */}
      <div className="of-card" style={{ marginBottom: 16, background: 'var(--bg-light, #f8fafc)' }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
          <div>
            <span style={{ color: 'var(--text-muted)' }}>Zeitraum: </span>
            <strong>{formatDate(survey.start_date)} – {formatDate(survey.end_date)}</strong>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)' }}>Jahr: </span>
            <strong>{survey.year}</strong>
          </div>
          {submission?.deadline_extension && (
            <div>
              <span style={{ color: '#b45309' }}>Verlängert bis: </span>
              <strong style={{ color: '#b45309' }}>{formatDate(submission.deadline_extension)}</strong>
            </div>
          )}
        </div>
      </div>

      {isReadOnly && (
        <div style={{ marginBottom: 16, padding: '10px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, fontSize: 13, color: '#065f46' }}
          data-testid="fixed-notice">
          Diese Meldung ist fixiert und kann nicht mehr geändert werden.
        </div>
      )}

      {isExpired && !isReadOnly && (
        <div style={{ marginBottom: 16, padding: '10px 16px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 13, color: '#92400e' }}
          data-testid="expired-notice">
          Der Eingabezeitraum ist abgelaufen. Änderungen sind nicht mehr möglich.
        </div>
      )}

      {/* Data Entry Table */}
      <div className="of-card" style={{ marginBottom: 16, padding: 0 }}>
        <div className="of-section" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          Teilnehmerzahlen nach Angebotsart
        </div>
        {offerTypes.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            Keine aktiven Angebote gefunden. Bitte wenden Sie sich an die BGPR-Geschäftsstelle.
          </div>
        ) : (
          <div className="table-wrap" style={{ borderTop: 'none' }}>
            <table data-testid="survey-data-table">
              <thead>
                <tr>
                  <th>Angebot</th>
                  <th style={{ width: 100, textAlign: 'center' }}>Gruppen</th>
                  <th style={{ width: 110, textAlign: 'center' }}>Weiblich</th>
                  <th style={{ width: 110, textAlign: 'center' }}>Männlich</th>
                  <th style={{ width: 100, textAlign: 'center' }}>Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <tr key={entry.offer_type} data-testid={`entry-row-${idx}`}>
                    <td style={{ fontWeight: 500, fontSize: 13 }}>{entry.offer_type}</td>
                    <td>
                      <div
                        style={{ textAlign: 'center', padding: '4px 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-light, #f1f5f9)', borderRadius: 4 }}
                        data-testid={`entry-groups-${idx}`}
                      >
                        {entry.group_count || 0}
                      </div>
                    </td>
                    <td>
                      <input
                        className="of-input"
                        type="number"
                        min="0"
                        value={entry.female_count || ''}
                        onChange={e => updateEntry(idx, 'female_count', e.target.value)}
                        disabled={!canEdit}
                        style={{ textAlign: 'center', padding: '4px 8px' }}
                        data-testid={`entry-female-${idx}`}
                      />
                    </td>
                    <td>
                      <input
                        className="of-input"
                        type="number"
                        min="0"
                        value={entry.male_count || ''}
                        onChange={e => updateEntry(idx, 'male_count', e.target.value)}
                        disabled={!canEdit}
                        style={{ textAlign: 'center', padding: '4px 8px' }}
                        data-testid={`entry-male-${idx}`}
                      />
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600, fontSize: 14 }}>
                      {entry.total_count || 0}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--bg-light, #f8fafc)', fontWeight: 600 }} data-testid="entry-totals">
                  <td style={{ fontSize: 13 }}>Gesamt</td>
                  <td style={{ textAlign: 'center' }}>{totalGroups}</td>
                  <td style={{ textAlign: 'center' }}>{totalFemale}</td>
                  <td style={{ textAlign: 'center' }}>{totalMale}</td>
                  <td style={{ textAlign: 'center', fontSize: 14 }}>{totalAll}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Texts */}
      {(survey.confirmation_texts || []).length > 0 && (
        <div className="of-card" style={{ marginBottom: 16 }} data-testid="survey-confirmations">
          <div className="of-section" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckSquare size={16} /> Bestätigungen
          </div>
          {survey.confirmation_texts.map(ct => (
            <label key={ct.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
              cursor: canEdit ? 'pointer' : 'default', fontSize: 13,
            }} data-testid={`confirm-${ct.id}`}>
              <input
                type="checkbox"
                checked={confirmedTexts.includes(ct.id)}
                onChange={() => canEdit && toggleConfirmation(ct.id)}
                disabled={!canEdit}
                style={{ marginTop: 2, flexShrink: 0 }}
                data-testid={`confirm-check-${ct.id}`}
              />
              <span>
                {ct.text}
                {ct.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Message */}
      {message && (
        <div style={{
          marginBottom: 16, padding: '10px 16px', borderRadius: 6, fontSize: 13,
          background: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          color: message.type === 'success' ? '#065f46' : '#991b1b',
        }} data-testid="survey-message">
          {message.text}
        </div>
      )}

      {/* Action Buttons */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="survey-actions">
          <button className="btn btn-secondary" onClick={() => handleSave('save')}
            disabled={saving} style={{ padding: '8px 20px' }} data-testid="survey-save-draft-btn">
            <Save size={14} /> {saving ? 'Speichern...' : 'Zwischenspeichern'}
          </button>
          <button className="btn btn-primary" onClick={() => handleSave('submit')}
            disabled={saving} style={{ padding: '8px 24px' }} data-testid="survey-submit-btn">
            <Send size={14} /> {saving ? 'Absenden...' : 'Meldung abgeben'}
          </button>
        </div>
      )}

      {submission?.admin_corrected_at && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
          Korrigiert durch Admin ({submission.admin_corrected_by}) am {formatDate(submission.admin_corrected_at)}
        </div>
      )}
    </div>
  );
}
